import type { AxiosError } from "axios";
import * as React from "react";
import { useCallback, useEffect, useState, useMemo } from "react";
import { StyleSheet, View, FlatList } from "react-native";
import { useFormikContext } from "formik";

import { Button, HStack, Panel, Typography, VStack } from "@smartrent/ui";

import { useNetsuiteDraftOrderMutation } from "@/api";
import { useUser } from "@/layout/Context";

import { formatCurrency } from "@/lib/helpers";
import type { NetsuiteDraftResponseData } from "@/types";

import { getShippingMethodLabel } from "../shipping-methods";
import type { ShippingMethodOption } from "../shipping-methods";

import type { NetsuiteFormValues as FormValues } from "../types";

import { AddressLine } from "../AddressLine";

import { NetsuiteOrderGrandTotal } from "./NetsuiteOrderGrandTotal";

interface AddressErrorMessageProps {
  errorMessage: string;
  retryCount: number;
  isRetryable: boolean;
  onRetry?: () => void;
  isRetryLoading?: boolean;
}

export const RETRYABLE_ERROR =
  "We're unable to verify your shipping address right now. Please try again.";
const MAX_RETRIES = 3;

const AddressErrorMessage: React.FC<AddressErrorMessageProps> = ({
  errorMessage,
  retryCount,
  isRetryable,
  onRetry,
  isRetryLoading,
}) => {
  if (isRetryable && retryCount >= MAX_RETRIES) {
    return (
      <Typography variation="regular.caption.medium" color="error">
        Address verification is unavailable. Please come back later.
      </Typography>
    );
  }
  return (
    <HStack spacing={4}>
      <Typography variation="regular.caption.medium" color="error">
        {errorMessage}
      </Typography>
      {isRetryable && onRetry ? (
        <Button
          variation="outlined"
          size="small"
          onPress={onRetry}
          isLoading={isRetryLoading}
        >
          Retry
        </Button>
      ) : null}
    </HStack>
  );
};

interface DraftState {
  isLoading: boolean;
  isError: boolean;
  draftData: NetsuiteDraftResponseData | undefined;
}

interface NetsuiteSummaryFormProps {
  shippingAddressError?: string | null;
  submitRetryCount?: number;
  onRetry?: () => void;
  isRetryLoading?: boolean;
  onDraftStateChange: (state: DraftState) => void;
  shippingMethods: readonly ShippingMethodOption[];
}

export const NetsuiteSummaryForm: React.FC<NetsuiteSummaryFormProps> = ({
  shippingAddressError,
  submitRetryCount,
  onRetry,
  isRetryLoading,
  onDraftStateChange,
  shippingMethods,
}) => {
  const user = useUser();
  const { values: formValues } = useFormikContext<FormValues>();

  const devices = useMemo(() => formValues.devices || [], [formValues.devices]);
  const netsuiteData = formValues.netsuiteData;
  const groupId = formValues.group_id;

  const [createDraft, { isLoading, data: draftData, error: draftError }] =
    useNetsuiteDraftOrderMutation();

  const [hasCalculated, setHasCalculated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    setHasCalculated(false);
  }, []);

  // Automatically calculate draft order totals
  useEffect(() => {
    if (
      !hasCalculated &&
      netsuiteData?.customerId &&
      groupId &&
      devices.length > 0 &&
      formValues.shipping_address_line1 &&
      formValues.shipping_address_city &&
      formValues.shipping_address_state &&
      formValues.shipping_address_zip &&
      formValues.shipping_method_slug &&
      formValues.contact_info_email
    ) {
      const lineItems = devices.map((device) => ({
        item_id: parseInt(device.id, 10),
        quantity: device.quantity,
      }));

      void createDraft({
        group_id: groupId,
        customer_id: netsuiteData.customerId,
        transaction_type: "WARRANTY_ORDER",
        purchase_order: formValues.contact_info_po_number || undefined,
        shipping_address_line1: formValues.shipping_address_line1,
        shipping_address_line2: formValues.shipping_address_line2 || undefined,
        shipping_address_city: formValues.shipping_address_city,
        shipping_address_state: formValues.shipping_address_state,
        shipping_address_zip: formValues.shipping_address_zip,
        shipping_method_slug: formValues.shipping_method_slug,
        is_custom_shipping_address:
          formValues.is_custom_shipping_address ?? false,
        order_confirmation_email: formValues.shipping_address_contact_email,
        online_portal_user: {
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
        },
        line_items: lineItems,
      });

      setHasCalculated(true);
    }
  }, [
    hasCalculated,
    netsuiteData,
    groupId,
    devices,
    formValues,
    user,
    createDraft,
  ]);
  useEffect(() => {
    onDraftStateChange({
      isLoading,
      isError: Boolean(draftError),
      draftData: draftData ?? undefined,
    });
  }, [isLoading, draftError, draftData, onDraftStateChange]);

  const draftErrorMessage = draftError
    ? (draftError as AxiosError<{ errors?: string[] }>).response?.data
        ?.errors?.[0] || draftError.message
    : null;

  return (
    <VStack spacing={24}>
      <Panel>
        <View style={styles.header}>
          <Typography type="title3">Order Summary</Typography>
          <Typography type="body">
            Please review your order before submitting
          </Typography>
        </View>

        <VStack spacing={16}>
          <View>
            <Typography type="caption">Customer</Typography>
            <Typography type="body">
              {netsuiteData?.customerName || "N/A"}
            </Typography>
          </View>

          <View>
            <Typography type="caption">Property</Typography>
            <Typography type="body">
              {netsuiteData?.propertyName || "N/A"}
            </Typography>
          </View>

          <View>
            <AddressLine
              variation="regular.caption.medium"
              isCustom={formValues.is_custom_shipping_address}
            >
              {formValues.is_custom_shipping_address
                ? "Custom Shipping Address"
                : "Shipping Address"}
            </AddressLine>
            <AddressLine isCustom={formValues.is_custom_shipping_address}>
              {formValues.shipping_address_line1}
            </AddressLine>
            {formValues.shipping_address_line2 && (
              <AddressLine isCustom={formValues.is_custom_shipping_address}>
                {formValues.shipping_address_line2}
              </AddressLine>
            )}
            <AddressLine isCustom={formValues.is_custom_shipping_address}>
              {formValues.shipping_address_city},{" "}
              {formValues.shipping_address_state}{" "}
              {formValues.shipping_address_zip}
            </AddressLine>
            {draftErrorMessage ? (
              <AddressErrorMessage
                errorMessage={draftErrorMessage}
                retryCount={retryCount}
                isRetryable={draftErrorMessage === RETRYABLE_ERROR}
                onRetry={handleRetry}
                isRetryLoading={isLoading}
              />
            ) : shippingAddressError ? (
              <AddressErrorMessage
                errorMessage={shippingAddressError}
                retryCount={submitRetryCount ?? 0}
                isRetryable={shippingAddressError === RETRYABLE_ERROR}
                onRetry={onRetry}
                isRetryLoading={isRetryLoading}
              />
            ) : null}
          </View>
        </VStack>
      </Panel>

      <Panel>
        <View style={styles.header}>
          <Typography type="title3">Equipment</Typography>
        </View>

        <FlatList
          data={devices}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.deviceRow}>
              <View style={styles.deviceDetails}>
                <Typography type="body" weight="semibold">
                  {item.name}
                </Typography>
                <Typography type="caption">
                  {formatCurrency(item.price)} × {item.quantity}
                </Typography>
              </View>
              <Typography type="body" weight="semibold">
                {formatCurrency(item.price * item.quantity)}
              </Typography>
            </View>
          )}
        />
      </Panel>

      <NetsuiteOrderGrandTotal
        isLoading={isLoading}
        subTotal={draftData?.response?.subTotal}
        taxTotal={draftData?.response?.taxTotal}
        shippingCost={draftData?.response?.shippingCost}
        orderTotal={draftData?.response?.orderTotal}
        shippingMethodLabel={getShippingMethodLabel(
          formValues.shipping_method_slug,
          shippingMethods
        )}
        error={draftError ? "Error calculating totals" : undefined}
      />
    </VStack>
  );
};

const styles = StyleSheet.create({
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  deviceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  deviceDetails: {
    flex: 1,
  },
});
